<template>
  <div>
    <form @submit.prevent class="input-field-filter text-medium" ref="searchForm">
      <label for="nql">NQL: </label>
      <input
        ref="nqlInput"
        v-model="nqlExpression"
        @keyup="checkNQLAutoComplete"
        @keyup.enter="triggerAutoCompleteSelection"
        @keydown.tab="preventFieldBlur"
        @keyup.tab="triggerAutoCompleteSelection"
        @search="triggerAutoCompleteSelection"
        @blur="onBlur"
        @click="checkNQLAutoComplete"
        @keyup.up="selectPreviousItem"
        @keyup.down="selectNextItem"
        @keydown.down.prevent
        @keydown.up.prevent
        autocomplete="off"
        class="nql-field"
        type="text"
        id="nql"
        placeholder="Type NQL and hit enter"
        v-autofocus
      />
      <div ref="listContainer">
        <auto-complete-list
          :meta="autoCompleteMeta"
          :left="listLeftPosition"
          :org-id="orgId"
          :sub-org-id="subOrgId"
          v-if="autoCompleteMeta"
          :filter-configuration="filterConfiguration"
          @select="onAutoCompleteSelection"
        />
      </div>
    </form>
    <div>
      <div class="text-small pl-2">
        <a href="https://thenorth.io/help/nql/" target="_blank">Learn more</a>
        OR
        <a href="#" @click="toggleAskNorthAI">Ask North AI</a>
      </div>
      <b-collapse v-model="showAskNorthAI" class="ask-north-ai-container my-2">
        <div class="px-3 py-1">
          <div class="text-small">
            <p>
              <strong>Experimental</strong> Ask North AI to make NQL for you (including custom
              fields). For example:
            </p>
            <ul>
              <li>Initiatives due by next week and status is not started</li>
              <li>Initiatives created this week and with a high priority</li>
            </ul>
            <p>
              Please <a href="mailto:support@thenorth.io">drop a note</a> if North AI does not work
              for you as expected.
            </p>
          </div>
          <div class="input-field-filter text-medium my-2 d-flex">
            <input
              v-model="askNorthAIText"
              type="text"
              class="form-input mr-2"
              placeholder="Ask North AI"
              @keyup.enter="askNorthAI"
            />
            <b-button variant="primary" pill :disabled="askNorthAIText === ''" @click="askNorthAI">
              <span v-if="askingNorthAI"><b-spinner small></b-spinner> Working...</span>
              <span v-else>Make NQL</span>
            </b-button>
          </div>
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { trackEvent } from '@/analytics'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { InitiativeFieldId } from '@/constants/initiative/filter'
import * as R from 'ramda'
import AutoCompleteList from '@/components/initiative/advanced-filter/AutoCompleteList.vue'
import ListNavigationMixin from '@/mixins/ListNavigationMixin'
import { autoCompleteParams } from '@/nql'
import getCaretCoordinates from 'textarea-caret'
import { BButton, BCollapse, BSpinner } from 'bootstrap-vue'
import service from '@/service'
import urlManager from '@/urlManager'

/**
 * This provides an input and an autocomplete list to build NQL expression
 */
export default {
  name: 'AdvancedFilter',
  props: ['filterConfiguration', 'orgId', 'subOrgId', 'value'],
  mixins: [ListNavigationMixin],
  components: {
    AutoCompleteList,
    BCollapse,
    BButton,
    BSpinner
  },
  data() {
    return {
      listLeftPosition: null,
      autoCompleteMeta: {},
      nqlExpression: this.value,
      showAskNorthAI: false,
      askNorthAIText: '',
      askingNorthAI: false
    }
  },
  computed: {
    listNode() {
      return this.$refs.listContainer
    }
  },
  methods: {
    updateListPosition(input) {
      const { left } = getCaretCoordinates(input, input.selectionEnd)
      this.listLeftPosition = left
    },
    checkNQLAutoComplete(event) {
      const input = event.target
      this.updateListPosition(input)
      //console.log('autocomlete selection start', input.selectionStart)
      const params = autoCompleteParams(input.value, input.selectionStart)
      //console.log('autocomplete params', params)
      this.autoCompleteMeta = params
    },
    preventFieldBlur(event) {
      let selected = this.getSelectedId()
      if (selected) {
        event.preventDefault()
      }
    },
    triggerAutoCompleteSelection() {
      let selected = this.getSelectedId()
      if (!R.isNil(selected)) {
        // FIXME: hack to ensure that integers are not quoted - ideally AutoCompleteList should be
        // listening for the enter keypress so it can emit the correct value. (It does so currently,
        // but only when the dropdown option is selected by a click, and not when enter is pressed).
        const selectedInt = parseInt(selected)
        if (!isNaN(selectedInt)) {
          selected = selectedInt
        }
        this.onAutoCompleteSelection(selected)
      } else {
        this.$refs.nqlInput.blur()
        this.$emit('change', this.nqlExpression)
        trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.NQL, EVENT_NAME.ALL_INITIATIVES)
      }
    },
    formatSelectedOption(value) {
      // if string contain space, enclose it within quotes
      if (typeof value === 'string') {
        return `"${value}"`
      }
      return value
    },
    onAutoCompleteSelection(selectedOption) {
      if (this.autoCompleteMeta.edge === 'field' && selectedOption === InitiativeFieldId.PRIORITY) {
        selectedOption = InitiativeFieldId.NEW_PRIORITY
      }
      let nqlExpression = this.nqlExpression
      let [firstHalf, lastHalf] = R.splitAt(this.autoCompleteMeta.start, nqlExpression)
      lastHalf = lastHalf.substring(this.autoCompleteMeta.end - this.autoCompleteMeta.start)
      let formattedOption =
        this.autoCompleteMeta.edge === 'values'
          ? this.formatSelectedOption(selectedOption)
          : selectedOption
      this.nqlExpression = `${firstHalf}${formattedOption} ${lastHalf}`
      let cursorPosition = `${firstHalf}${formattedOption} `.length
      const inputElement = document.getElementById('nql')
      setTimeout(function() {
        inputElement.focus()
        inputElement.setSelectionRange(cursorPosition, cursorPosition)
      }, 100)
      this.clearAutoCompleteMeta()
    },
    clearAutoCompleteMeta() {
      this.autoCompleteMeta = false
    },
    onBlur() {
      this.clearAutoCompleteMeta()
    },
    toggleAskNorthAI(event) {
      event.preventDefault()
      this.showAskNorthAI = !this.showAskNorthAI
    },
    async askNorthAI() {
      this.askingNorthAI = true
      try {
        const response = await service.client.post(urlManager.generateNql(this.orgId), {
          prompt: this.askNorthAIText
        })
        this.nqlExpression = response.data.nql
        this.triggerAutoCompleteSelection()
      } finally {
        this.askingNorthAI = false
      }
    }
  },
  mounted() {},
  watch: {
    value(newValue) {
      this.nqlExpression = newValue
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/filter';

.ask-north-ai-container {
  border: 1px solid #e8e8e8;
  border-radius: 10px;
}
</style>
